/**
 * Accordion Groups (Savjeti i informacije)
 *
 * 1) Opens the matching <details> (and its parent group) when the URL hash
 *    points to a group id or an individual item id, and scrolls it into
 *    view. Unchanged from before Brief O.
 *
 * 2) Per-item hover image (Brief O, 2026-08-16) — reuses the exact same
 *    "single sticky <img>, JS swaps its src on mouseenter" pattern already
 *    approved on /o-nama/ (components/anchor-sections.js): only wired up
 *    on devices that report real hover + a fine pointer (desktop
 *    mouse/trackpad); touch devices never get the listeners, so behaviour
 *    there is unchanged from a plain accordion. New here: the sticky image
 *    also nudges gently towards the cursor position within the hovered
 *    row (transform + requestAnimationFrame, lerp-damped — never
 *    `top`/`left`, never `setInterval`), skipped entirely under
 *    `prefers-reduced-motion: reduce`.
 *
 * 3) Hides the sticky hover image while any item in the block is open
 *    (the per-item open-image, shown by pure CSS on `[open]`, already
 *    covers that case) — listens to native `toggle` events on every
 *    <details> and toggles a class on the block root.
 *
 * Round 2 (Brief O follow-up, 2026-08-18) — operator asked for the
 * accordion back at full width and the hover image to become an absolute
 * overlay (no more dedicated grid column). The image element itself is
 * unchanged (still one shared <img>, still src-swapped, still the same
 * mouse-follow nudge); what's new is `positionMediaFor()`, which sets the
 * *base* position (`top`, in px, on the `.accordion-groups__media`
 * wrapper) to align with whichever row is currently hovered, since the
 * media element is no longer sticky-following scroll — it's a plain
 * `position: absolute` box overlaid on `.accordion-groups__row`.
 *
 * Round 3 (same day, operator follow-up) — bigger image (CSS, see SCSS),
 * bigger mouse-follow travel, and a smoother/"ease-out" feel to the
 * movement. Two separate levers, per the operator's own breakdown:
 *  1. The *base* position change (`top`, above) now transitions in CSS
 *     with the site's shared ease-out curve instead of snapping — see
 *     SCSS `&__media` under `prefers-reduced-motion: no-preference`.
 *  2. The *continuous* per-frame mouse-follow nudge below is lerp-damped,
 *     not CSS-transitioned (it's updated every animation frame, so a CSS
 *     transition on it would fight the per-frame updates) — smoothing it
 *     out means lowering LERP so each frame closes less of the remaining
 *     distance, producing a longer, softer trail behind the cursor.
 */
export default function accordionGroups() {
	const root = document.querySelector('.accordion-groups');
	if (!root) {
		return;
	}

	const openFromHash = () => {
		const hash = window.location.hash ? window.location.hash.substring(1) : '';
		if (!hash) {
			return;
		}

		const target = document.getElementById(hash);
		if (!target) {
			return;
		}

		if (target.tagName === 'DETAILS') {
			target.open = true;
		}

		const parentDetails = target.closest('details.accordion-groups__item');
		if (parentDetails) {
			parentDetails.open = true;
		}

		window.setTimeout(() => {
			target.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 50);
	};

	root.querySelectorAll('[data-accordion-nav]').forEach((link) => {
		link.addEventListener('click', () => {
			root.querySelectorAll('[data-accordion-nav]').forEach((l) => l.classList.remove('is-active'));
			link.classList.add('is-active');
		});
	});

	window.addEventListener('hashchange', openFromHash);
	openFromHash();

	// --- Hide the sticky hover column while anything is open (§3) ---
	const updateOpenState = () => {
		const anyOpen = !!root.querySelector('.accordion-groups__item[open]');
		root.classList.toggle('accordion-groups--has-open', anyOpen);
	};

	root.querySelectorAll('.accordion-groups__item').forEach((details) => {
		details.addEventListener('toggle', updateOpenState);
	});
	updateOpenState(); // covers the hash-open case above, which sets `.open` before this runs

	// --- Hover image + mouse-follow (§2) ---
	const mediaContainer = root.querySelector('.accordion-groups__media');
	const mediaImg = root.querySelector('.accordion-groups__media-img');
	const rowContainer = root.querySelector('.accordion-groups__row');
	const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

	if (!mediaContainer || !mediaImg || !rowContainer || !canHover) {
		return;
	}

	// Base position (Round 2): align the absolutely-positioned media box
	// with the vertical center of whichever row is hovered, clamped so it
	// never sits above the top or spills below the bottom of the whole
	// list. Computed fresh on every `mouseenter` from live
	// `getBoundingClientRect()` values (both viewport-relative, so their
	// difference is scroll-independent) — cheap, and avoids having to
	// track layout changes (rows opening/closing shift row positions).
	const positionMediaFor = (row) => {
		const rowRect = row.getBoundingClientRect();
		const containerRect = rowContainer.getBoundingClientRect();
		const mediaRect = mediaContainer.getBoundingClientRect();

		let top = rowRect.top - containerRect.top + rowRect.height / 2 - mediaRect.height / 2;
		const maxTop = Math.max(0, containerRect.height - mediaRect.height);
		top = Math.max(0, Math.min(top, maxTop));

		mediaContainer.style.top = `${top}px`;
	};

	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const rows = root.querySelectorAll('.accordion-groups__item--has-image[data-hover-image]');
	if (!rows.length) {
		return;
	}

	// Amplitude/damping (Round 4, Brief Q QA round 2, 2026-08-27: bumped up
	// again from Round 3's 26px / 0.1 — operator asked for slower motion
	// with MORE travel. Bigger AMPLITUDE = the cursor position maps to a
	// wider swing; lower LERP = each animation frame closes less of the
	// remaining distance, so the image trails the cursor longer/softer
	// before settling). Still damped (lerp), not 1:1 tracking.
	const AMPLITUDE = 32;
	const LERP = 0.07;

	let targetX = 0;
	let targetY = 0;
	let currentX = 0;
	let currentY = 0;
	let rafId = null;
	let activeRow = null;

	const applyTransform = () => {
		currentX += (targetX - currentX) * LERP;
		currentY += (targetY - currentY) * LERP;
		mediaImg.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0)`;
		rafId = window.requestAnimationFrame(applyTransform);
	};

	const startTracking = () => {
		if (rafId === null) {
			rafId = window.requestAnimationFrame(applyTransform);
		}
	};

	const stopTracking = () => {
		if (rafId !== null) {
			window.cancelAnimationFrame(rafId);
			rafId = null;
		}
		targetX = 0;
		targetY = 0;
		currentX = 0;
		currentY = 0;
		mediaImg.style.transform = '';
	};

	const handleMouseMove = (event) => {
		if (!activeRow) {
			return;
		}
		const rect = activeRow.getBoundingClientRect();
		const relX = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
		const relY = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
		targetX = Math.max(-1, Math.min(1, relX)) * AMPLITUDE;
		targetY = Math.max(-1, Math.min(1, relY)) * AMPLITUDE;
	};

	rows.forEach((row) => {
		const hoverSrc = row.getAttribute('data-hover-image');

		row.addEventListener('mouseenter', () => {
			activeRow = row;
			positionMediaFor(row);
			if (mediaImg.getAttribute('src') !== hoverSrc) {
				mediaImg.src = hoverSrc;
			}
			mediaImg.classList.add('is-visible');

			if (!reduceMotion) {
				row.addEventListener('mousemove', handleMouseMove);
				startTracking();
			}
		});

		row.addEventListener('mouseleave', () => {
			activeRow = null;
			mediaImg.classList.remove('is-visible');

			if (!reduceMotion) {
				row.removeEventListener('mousemove', handleMouseMove);
				stopTracking();
			}
		});
	});
}
